import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  TableContainer,
  Paper,
  Grid,
  Dialog,
  TextField,
  DialogContent,
  CircularProgress,
  Typography,
  Autocomplete,
} from "@mui/material";
import MDBox from "components/MDBox";
import { writeFile, utils } from "xlsx";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  inputStyle,
  StyledAgGridContainer,
} from "components/StyledComponents/styledComponents";
import { ledgerStyles } from "components/Styles/LedgerStyles";
import {
  selectScrollStyle,
  CustomPaper,
  autoSelectStyle,
  globalStyles,
} from "components/Styles/globalStyles";
import URLs from "constants/urls";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { useMaterialUIController } from "context";

export default function Ledger() {
  const [ledgerData, setLedgerData] = useState([]);
  const [pinnedBottomData, setPinnedBottomData] = useState([]);
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [companies, setCompanies] = useState([]);
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [selectedLedger, setSelectedLedger] = useState("");
  const gridRef = useRef(null);

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 2);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const formatDate = (d) => d.toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(formatDate(firstDayOfMonth));
  const [toDate, setToDate] = useState(formatDate(lastDayOfMonth));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const MInput = inputStyle();
  const theme = useTheme();

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const computeAndPin = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setPinnedBottomData([]);
      return;
    }

    const totalDebit = rows.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0);
    const totalCredit = rows.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0);

    // ✅ Last row's balance = remaining balance
    const remainingBalance = parseFloat(rows[rows.length - 1]?.balance) || 0;

    setPinnedBottomData([
      {
        date: "",
        ledgerName: "Total",
        remarks: "",
        debit: totalDebit,
        credit: totalCredit,
        balance: remainingBalance, // ✅ last row balance
        balanceType: rows[rows.length - 1]?.balanceType || "", // ✅ last row balanceType
      },
    ]);
  }, []);
  const onFilterChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    const visibleRows = [];
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeAndPin(visibleRows);
  }, [computeAndPin]);

  const fetchLedgerAccounts = async () => {
    try {
      const res = await fetch(URLs.GetLedgerAccounts);
      if (!res.ok) throw new Error("Failed to fetch ledger accounts");
      setLedgerAccounts(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await fetch(URLs.GetCompanies);
      const data = await res.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setCompanies([]);
    }
  };

  const fetchLedgerData = async () => {
    if (!fromDate || !toDate) {
      setAlert("Please select all required fields", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(URLs.getLedger, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyID: user.companyId,
          ledgerID: selectedLedger,
          fromDate,
          toDate,
        }),
      });
      if (!res.ok) throw new Error("Failed to fetch ledger data.");

      let data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        data = data.slice(0, -1);
      }

      setLedgerData(data);
      computeAndPin(data);
    } catch (e) {
      setAlert("Failed to fetch ledger data. Please try again", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedgerAccounts();
    fetchCompanies();
  }, []);

  const downloadLedgerData = () => {
    if (!ledgerData.length) return;
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(ledgerData);
    utils.book_append_sheet(wb, ws, "LedgerData");
    writeFile(wb, "Ledger_Data.xlsx");
  };
  const fmtINR = (val) => {
    const n = parseFloat(val);
    return isNaN(n)
      ? "₹ 0.00"
      : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const getRowStyle = (params) => {
    if (params.node.rowPinned === "bottom") {
      return {
        background: "#E59400",
        color: "#ffffff",
        fontWeight: "800",
        fontSize: "13px",
        borderTop: "2px solid #E59400",
      };
    }
    return null;
  };
  const columnDefs = [
    {
      headerName: "Date",
      field: "date",
      minWidth: 100,
      filter: true,
      sortable: true,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const d = new Date(params.value);
        return `${d.getDate().toString().padStart(2, "0")}-${d.toLocaleString("default", {
          month: "short",
        })}-${d.getFullYear()}`;
      },
    },
    {
      headerName: "Ledger Name",
      field: "ledgerName",
      minWidth: 150,
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Remarks",
      field: "remarks",
      minWidth: 150,
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Debit",
      field: "debit",
      minWidth: 120,
      flex: 1,
      filter: true,
      sortable: true,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Credit",
      field: "credit",
      minWidth: 120,
      flex: 1,
      filter: true,
      sortable: true,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Balance",
      field: "balance",
      minWidth: 120,
      flex: 1,
      filter: true,
      sortable: true,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Balance Type",
      field: "balanceType",
      maxWidth: 130,
      filter: true,
      sortable: true,
    },
  ];
  return (
    <DashboardLayout>
      {/* Loading */}
      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      <Grid item xs={12} md={6}>
        <TableContainer component={Paper} sx={globalStyles.accountsTable}>
          {/* Filter bar */}
          <MDBox sx={ledgerStyles.mdBoxStyle} mt={1} mb={2}>
            <Grid container spacing={2} alignItems="center" wrap="wrap">
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  label="From Date"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  fullWidth
                  sx={MInput.b}
                  InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  label="To Date"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  fullWidth
                  sx={MInput.b}
                  InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  options={ledgerAccounts}
                  getOptionLabel={(option) => option.accountName || ""}
                  value={ledgerAccounts.find((i) => i.accountID === selectedLedger) || null}
                  onChange={(_, v) => setSelectedLedger(v?.accountID || "")}
                  filterOptions={(options, { inputValue }) => {
                    const input = inputValue.toLowerCase();
                    return options
                      .sort((a, b) => {
                        const aS = a.accountName.toLowerCase().startsWith(input);
                        const bS = b.accountName.toLowerCase().startsWith(input);
                        return aS === bS ? 0 : aS ? -1 : 1;
                      })
                      .filter((o) => o.accountName.toLowerCase().includes(input));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Ledger"
                      variant="outlined"
                      fullWidth
                      required
                      sx={{ height: 43, ...autoSelectStyle, ...MInput.b }}
                      InputLabelProps={{ sx: { color: MInput.a } }}
                    />
                  )}
                  PaperComponent={CustomPaper}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <StyledButton type="submit" onClick={fetchLedgerData} fullWidth>
                  Display
                </StyledButton>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6} md={2} mt={1}>
              <StyledButton onClick={downloadLedgerData}>
                <DownloadForOfflineIcon fontSize="medium" />
              </StyledButton>
            </Grid>
          </MDBox>

          {/* AG Grid */}
          <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              enableCellTextSelection={true}
              enableRangeSelection={true}
              columnDefs={columnDefs}
              rowData={ledgerData}
              pagination={true}
              paginationPageSize={12}
              pinnedBottomRowData={pinnedBottomData}
              onFilterChanged={onFilterChanged}
              getRowStyle={getRowStyle}
            />
          </StyledAgGridContainer>
        </TableContainer>
      </Grid>
    </DashboardLayout>
  );
}
